import React, { useState } from 'react';
import { v4 as uuid } from 'uuid';
import AsyncRetry from 'async-retry';
import { useApolloClient, useMutation } from '@apollo/client';

import { MethodProps } from '../types';
import { useI18n, useLogger, useSelector } from '~Hooks';
import { useStorage } from '~Components/Storage';
import NumberInput from '~ComponentsImproved/Numpad';
import GiftCardPayment, { TAB_TYPES } from '~Components/GiftCard/Payment';
import {
  GET_GIFT_CARD_PAYMENT,
  GIFT_CARD_BY_SHORTCODE,
  PAY_WITH_GIFT_CARD,
} from '~Queries/gift-cards';
import {
  GetGiftCardByShortCode,
  GetGiftCardByShortCodeVariables,
  GetGiftCardPayment,
  GetGiftCardPaymentVariables,
  PayWithGiftCard,
  PayWithGiftCardVariables,
} from '~Queries/__queries__';
import { useGiftCardsContext } from '~Scenes/GiftCards/GiftCardsProvider';

const BaemingoGiftCardPayment: React.FunctionComponent<MethodProps> = ({
  payment,
  amount,
  onMetaChange,
  onComplete,
  onFail,
  onCancel,
  venueId,
}) => {
  const counter = useSelector((state) => state.POSReceiptCounter);
  const logger = useLogger('baemingo-gift-card-payment');
  const client = useApolloClient();
  const [storage] = useStorage();
  const { i18n } = useI18n();
  const universeId = useGiftCardsContext().universeId;

  const [isFailing, setIsFailing] = useState(false);
  const [showTipModal, setShowTipModal] = useState(!payment.disableTip && storage.enableTipping);

  // Pay with giftcard
  const [payWithGiftCard, { loading: payWithGiftCardLoading }] = useMutation<
    PayWithGiftCard,
    PayWithGiftCardVariables
  >(PAY_WITH_GIFT_CARD);

  // If the payment that spawned this is completed, don't render this
  if (payment.status === 'COMPLETED') {
    return null;
  }

  // Used to poll for updates on a gift card payment
  const getGiftCardPayment = (variables: GetGiftCardPaymentVariables) =>
    client.query<GetGiftCardPayment, GetGiftCardPaymentVariables>({
      query: GET_GIFT_CARD_PAYMENT,
      variables,
      fetchPolicy: 'network-only',
    });

  function onFailAndClose() {
    setIsFailing(true);
    onFail();
  }

  if (showTipModal) {
    return (
      <NumberInput
        key="tip"
        initialValue={'0'}
        decimal
        title={i18n('TM.Tip')}
        onSubmit={(tipAmount) => {
          setShowTipModal(false);

          // Ensure input is valid
          const tip = parseFloat(tipAmount);
          if (isNaN(tip)) {
            onFail();
            return;
          }

          onMetaChange({ tip: tip * 100 });

          // Slight delay so meta has time to update
          setTimeout(() => setShowTipModal(false), 1000);
        }}
        onClose={() => {
          setShowTipModal(false);
          onFail(i18n('Payment.Canceled'));
        }}
      />
    );
  }

  /**
   * @param shortcode The unique identifier for the gift card, 9 characters
   * @param amount The amount required for the purchase
   */
  const makePaymentFunction = async (
    shortcode: string,
    amount: number,
  ): Promise<{ success: boolean; message?: string; transactionId?: string }> => {
    // Create a reference for this payment
    const reference = uuid();

    const paymentPayload = {
      universeId,
      shortcode,
      amount,
      venueId,
      reference,
    };

    logger.info(`Giftcard payment: CARD #${shortcode} | Starting payment...`, {
      paymentPayload,
    });

    let paymentResult;

    try {
      paymentResult = await payWithGiftCard({ variables: paymentPayload });
    } catch (e) {
      logger.error(`Giftcard payment: CARD #${shortcode} | payWithGiftCard failed`, {
        e,
      });
    }

    if (paymentResult && paymentResult.data?.payWithGiftcard.success) {
      // Everything worked out, log success and return
      logger.info(`Giftcard payment: CARD #${shortcode} | Payment successful`, {
        ...paymentResult.data,
        amount,
        reference,
      });
      return { success: true, transactionId: paymentResult.data.payWithGiftcard.transactionId };
    }

    // If we don't return above, it means the payment failed
    logger.error(`Giftcard payment: CARD #${shortcode} | payWithGiftCard failed`, {
      paymentResult,
    });

    // Start polling the payment status
    const response = await AsyncRetry(
      async () => {
        return await getGiftCardPayment({
          ...paymentPayload,
          shortcode: paymentPayload.shortcode,
        }).then((res) => {
          if (!res?.data?.Giftcard) {
            throw new Error('Could not find gift card data on payment');
          }
          return res;
        });
      },
      { retries: 4, randomize: true, minTimeout: 5000 },
    );

    // Log the polling result
    logger.info(`Giftcard transaction status: CARD #${shortcode} | Polling result`, { response });

    // This call does not return the success bool, if both gift card id and payment id is set => success
    return {
      success: !!response.data.Giftcard?.id && !!response.data.Giftcard?.payment?.id,
      transactionId: response.data.Giftcard.payment.id,
    };
  };

  const tip = payment.meta.tip;
  return (
    <GiftCardPayment
      id={counter.toString()}
      amount={amount! + (tip ?? 0)}
      isFailing={isFailing}
      onFail={onFailAndClose}
      onComplete={(transactionId) =>
        onComplete(null, {
          transactionId,
        })
      }
      onCancel={onCancel}
      availableTabTypes={[TAB_TYPES.MANUAL, TAB_TYPES.QR]}
      getAvailableBalance={(cardNumber) =>
        new Promise((resolve, reject) => {
          client
            .query<GetGiftCardByShortCode, GetGiftCardByShortCodeVariables>({
              query: GIFT_CARD_BY_SHORTCODE,
              variables: {
                universeId,
                shortcode: cardNumber,
              },
              fetchPolicy: 'network-only',
            })
            .then(({ data, errors }) => {
              if (errors) {
                reject(errors[0].message);
              }

              if (data.Giftcard === null) {
                reject('Gift card not found');
              }

              resolve(data.Giftcard?.balance);
            })
            .catch((err) => {
              reject(err.message);
            });
        })
      }
      makePurchase={makePaymentFunction}
      cardNumberFormat="### ### ###"
      cardNumberMinLength={1}
    />
  );
};

export default BaemingoGiftCardPayment;
